import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import ListItemsTable from '../../../../../features/components/ListItemsTable'
import ListItemsTotals from '../../../../../features/components/ListItemsTotals'
import AuthorizePlatformDetails from '../../../../SocialAuthorize/AuthorizePlatformDetails'
import ProviderColumn from '../../../../../features/components/ListItemsTable/Columns/ProviderColumn'

import useGetRightCampaignActions from './useGetRightCampaignActions'

import { createCampaignSummaryPageLink } from '../../../../Campaigns/helpers'

import {
  combinedCampaignsErrorSelector,
  combinedCampaignsIsLoadingSelector,
  combinedCampaignsSelector,
  combinedCampaignsWasLoadedSelector
} from '../../../../../modules/selectors/combinedData'
import { partnershipAccountTypeSelector } from '../../../../../modules/selectors/app'
import {
  campaignDeleteItemSelector,
  campaignUpdateIsLoadingSelector,
  campaignUpdateSelector
} from '../../../../../modules/selectors/campaigns'

import {
  FACEBOOK_PROVIDER_PLATFORM,
  PLATFORM_LABELS,
  PROVIDER_TO_PLATFORMS,
  TIKTOK_PROVIDER_PLATFORM
} from '../../../../../constants/selectLists/platformList'
import { listItemsTableColumnsMinWidth } from '../../../../../features/components/ListItemsTable/columnsWidth'

import { RIGHTS_HOLDER_TYPE } from '../../../../../constants/selfAccounts'

import useStyles from './styles'

const RightCampaignsTable = ({ onRowCheck, handleDeleteRequest }) => {
  const classes = useStyles()
  const {
    campaigns = [],
    campaigns_summary: totalsItem,
    currency_symbol: combinedCampaignsCurrencySymbol
  } = useSelector(combinedCampaignsSelector)
  const campaignUpdate = useSelector(campaignUpdateSelector)
  const accountType = useSelector(partnershipAccountTypeSelector)
  const campaignUpdateIsLoading = useSelector(campaignUpdateIsLoadingSelector)
  const campaignDeleteItem = useSelector(campaignDeleteItemSelector)

  const campaignDeleteItemId = campaignDeleteItem && campaignDeleteItem.id
  const campaignDeleteItemIsLoading = campaignDeleteItem && campaignDeleteItem.isLoading

  const { id: campaignUpdateId } = campaignUpdate

  const showTotalsItem = totalsItem && campaigns.length > 1

  // if no currency symbol provided on high-level, use symbol of first campaign in the list
  const currencySymbol = combinedCampaignsCurrencySymbol
    ? combinedCampaignsCurrencySymbol
    : (!!campaigns.length && campaigns[0]['currency_symbol']) || ''

  const getRightCampaignActions = useGetRightCampaignActions(handleDeleteRequest)

  const purifiedCampaigns = useMemo(() => {
    // if campaigns has errors - just show one campaign with error per provider instead of all campaigns
    const firstErrorCampaignPerProvider = {}
    const nonErrorCampaigns = []

    campaigns.forEach(campaign => {
      if (campaign.error) {
        // Check if an error campaign for this provider has already been added
        if (!firstErrorCampaignPerProvider[campaign.provider]) {
          firstErrorCampaignPerProvider[campaign.provider] = campaign
        }
      } else {
        nonErrorCampaigns.push(campaign)
      }
    })

    // Combine the first error campaign per provider with all non-error campaigns
    return [...nonErrorCampaigns, ...Object.values(firstErrorCampaignPerProvider)]
  }, [campaigns])

  const errorColumns = useMemo(
    () => [
      {
        Component: ({ provider }) => <ProviderColumn provider={provider} />,
        style: {
          minWidth: listItemsTableColumnsMinWidth.platform,
          maxWidth: listItemsTableColumnsMinWidth.platform,
          paddingLeft: 5,
          paddingRight: 5
        }
      },
      {
        Component: ({ provider: itemProvider, error, error_code }) => {
          const itemPlatform = PROVIDER_TO_PLATFORMS[itemProvider]
          const isTokenExpired = error_code === 'token_expired'

          if (accountType === RIGHTS_HOLDER_TYPE && isTokenExpired) {
            // RH never needs to see any error telling them their token has expired. Means nothing to them - just show the connect button
            return (
              <AuthorizePlatformDetails
                selectedPlatform={PROVIDER_TO_PLATFORMS[itemProvider]}
                hasPadding={false}
                buttonClassName={classes.connectBtn}
              />
            )
          } else if (error_code === 'token_expired' && isTokenExpired) {
            return `There was an authorisation error from ${PLATFORM_LABELS[itemPlatform]}, the data cannot be displayed`
          } else {
            // What comes from FB like it currently works for FB errors. Error overrides for FB are done on the backed, we have functionality for that
            return error?.detail || error
          }
        }
      }
    ],
    [classes.connectBtn, accountType]
  )

  const getShowRowCheckbox = useCallback(provider => {
    return provider === FACEBOOK_PROVIDER_PLATFORM
  }, [])

  const getShowGoalCell = useCallback(provider => {
    return provider === FACEBOOK_PROVIDER_PLATFORM || provider === TIKTOK_PROVIDER_PLATFORM
  }, [])

  return (
    <ListItemsTable
      items={purifiedCampaigns}
      errorColumns={errorColumns}
      currencySymbol={currencySymbol}
      itemDeletingId={campaignDeleteItemIsLoading ? campaignDeleteItemId : ''}
      isLoadingSelector={combinedCampaignsIsLoadingSelector}
      errorSelector={combinedCampaignsErrorSelector}
      wasLoadedSelector={combinedCampaignsWasLoadedSelector}
      itemUpdatingId={campaignUpdateIsLoading ? campaignUpdateId : ''}
      createItemLinkHelper={createCampaignSummaryPageLink}
      getActionsDropdownOptions={getRightCampaignActions}
      noDataText="There are currently no campaigns associated with this digital right. To get started, please upload an ad creative."
      showRowCheckbox={true}
      onRowCheck={onRowCheck}
      getShowRowCheckbox={getShowRowCheckbox}
      showGoal={true}
      getShowGoal={getShowGoalCell}
      useConversionResultsField={true}
    >
      {showTotalsItem && (
        <ListItemsTotals
          items={purifiedCampaigns}
          showRowCheckbox={true}
          data={totalsItem}
          currencySymbol={currencySymbol}
        />
      )}
    </ListItemsTable>
  )
}

export default RightCampaignsTable
